import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Nav.css';
import { GOLD, INK, PUBLIC } from '../theme';

const SECTION_LINKS = [['Collections', '#collections'], ['Categories', '#categories'], ['Visit', '#visit']];

export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const onLanding = pathname === '/';

  // Lock the page underneath while the drawer is open, and let Escape close it.
  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const sectionLink = (label, hash, style, onClick) =>
    onLanding ? (
      <a key={label} href={hash} className="ij-nav-link" style={style} onClick={onClick}>{label}</a>
    ) : (
      <Link key={label} to={'/' + hash} className="ij-nav-link" style={style} onClick={onClick}>{label}</Link>
    );

  const drawerLinkStyle = { fontWeight: 600, fontSize: 17, letterSpacing: '.16em', textTransform: 'uppercase', color: '#e6ddc8', textDecoration: 'none', padding: '16px 0', borderBottom: '1px solid rgba(201,162,75,.14)' };

  return (
    <>
      {/* First tab stop on every page: lets a keyboard user jump the logo,
          five nav links and the CTA to reach the actual content. */}
      <a className="ij-skip" href="#ij-main">Skip to content</a>

      <nav style={{ position: 'sticky', top: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px clamp(20px,4vw,60px)', background: 'linear-gradient(180deg,rgba(16,12,8,.78),rgba(16,12,8,.32))', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(201,162,75,.2)' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: "'Bodoni Moda', serif", fontWeight: 700, fontSize: 23, letterSpacing: '.06em', color: '#e6ddc8', textDecoration: 'none' }}>
          <img src={PUBLIC + '/logo192.png'} alt="" style={{ height: 'clamp(44px,6vw,60px)', width: 'clamp(44px,6vw,60px)', borderRadius: '50%' }} />
          IJ COUTURE
        </Link>
        <div className="ij-nav-links" style={{ display: 'flex', gap: 'clamp(20px,2.8vw,44px)', alignItems: 'center' }}>
          {SECTION_LINKS.map(([label, href]) => sectionLink(label, href, { fontWeight: 600, fontSize: 14, letterSpacing: '.26em', textTransform: 'uppercase', color: '#e6ddc8', textDecoration: 'none' }))}
          <Link to="/about" className="ij-nav-link" aria-current={pathname === '/about' ? 'page' : undefined} style={{ fontWeight: 600, fontSize: 14, letterSpacing: '.26em', textTransform: 'uppercase', color: pathname === '/about' ? '#e2be76' : '#e6ddc8', textDecoration: 'none' }}>About</Link>
          <Link to="/book-appointment" aria-current={pathname === '/book-appointment' ? 'page' : undefined} style={{ fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '11px 20px', background: GOLD, color: INK, textDecoration: 'none', display: 'inline-block' }}>Book Appointment</Link>
        </div>
        <button className="ij-burger" aria-label="Toggle menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)} style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 8, flexDirection: 'column', gap: 5, zIndex: 1001, position: 'relative' }}>
          <span style={{ display: 'block', width: 24, height: 2, background: '#e6ddc8', transition: 'transform .25s ease', transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }} />
          <span style={{ display: 'block', width: 24, height: 2, background: '#e6ddc8', opacity: menuOpen ? 0 : 1, transition: 'opacity .2s ease' }} />
          <span style={{ display: 'block', width: 24, height: 2, background: '#e6ddc8', transition: 'transform .25s ease', transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
        </button>
      </nav>

      {/* Backdrop */}
      <div
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
        className="ij-drawer-backdrop"
        style={{ opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none' }}
      />

      {/* Side drawer — fixed to the viewport, so it always overlays fully
          regardless of how far the page underneath is scrolled. */}
      <div
        className="ij-drawer"
        style={{ transform: menuOpen ? 'translateX(0)' : 'translateX(100%)', visibility: menuOpen ? 'visible' : 'hidden' }}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!menuOpen}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px 28px clamp(24px,5vh,40px)' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
            <button onClick={() => setMenuOpen(false)} aria-label="Close menu" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: '#e6ddc8', fontSize: 26, lineHeight: 1 }}>×</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {SECTION_LINKS.map(([label, href]) => sectionLink(label, href, drawerLinkStyle, () => setMenuOpen(false)))}
            <Link to="/about" onClick={() => setMenuOpen(false)} className="ij-nav-link" aria-current={pathname === '/about' ? 'page' : undefined} style={drawerLinkStyle}>About</Link>
          </div>
          <Link to="/book-appointment" onClick={() => setMenuOpen(false)} style={{ marginTop: 'auto', fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '16px 20px', background: GOLD, color: INK, textDecoration: 'none', textAlign: 'center', width: '100%', display: 'block', boxSizing: 'border-box' }}>Book Appointment</Link>
        </div>
      </div>
    </>
  );
}
